import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { 
  TrendingUp, 
  PiggyBank,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Target,
  CheckCircle2
} from "lucide-react";
import { toast } from "sonner";

export const PlanPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/plan/summary");
      setPlan(response.data);
    } catch (error) {
      console.error("Failed to fetch plan:", error);
      toast.error("Failed to load plan");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  const formatCurrency = (amount) => {
    const currency = user?.currency || "CAD";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="plan-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
      </div>
    );
  }

  const savingsPositive = (plan?.net_savings || 0) >= 0;
  const savingsRateGood = (plan?.savings_rate || 0) >= 20;
  const budgetOnTrack = (plan?.budget_adherence || 0) >= 80;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="plan-page">
      {/* Header */}
      <header>
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight">Plan</h1>
        <p className="text-muted-foreground mt-1">
          {new Date().toLocaleDateString("en-CA", { month: "long", year: "numeric" })} snapshot
        </p>
      </header>

      {/* Key Metrics Grid */}
      <section className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Card className="border-border/50" data-testid="income-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <ArrowDownRight className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
            <p className="overline">Income</p>
            <p className="metric-value amount-positive mt-1">
              {formatCurrency(plan?.total_income || 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="border-border/50" data-testid="expenses-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <p className="overline">Expenses</p>
            <p className="metric-value mt-1">
              {formatCurrency(plan?.total_expenses || 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="border-border/50" data-testid="savings-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                savingsPositive ? "bg-primary/10" : "bg-red-100 dark:bg-red-900/30"
              }`}>
                <PiggyBank className={`w-5 h-5 ${savingsPositive ? "text-primary" : "text-red-600"}`} />
              </div>
            </div>
            <p className="overline">Net Savings</p>
            <p className={`metric-value mt-1 ${savingsPositive ? "text-primary" : "text-red-600"}`}>
              {savingsPositive ? "+" : ""}{formatCurrency(plan?.net_savings || 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="border-border/50" data-testid="leftover-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <p className="overline">Leftover</p>
            <p className="metric-value text-blue-600 dark:text-blue-400 mt-1">
              {formatCurrency(plan?.leftover || 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">After essentials</p>
          </CardContent>
        </Card>
      </section>

      {/* Progress Cards */}
      <section className="grid gap-6 md:grid-cols-2">
        <Card className="border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                budgetOnTrack ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-amber-100 dark:bg-amber-900/30"
              }`}>
                <Target className={`w-5 h-5 ${budgetOnTrack ? "text-emerald-600" : "text-amber-600"}`} />
              </div>
              <div>
                <h3 className="font-semibold">Budget Adherence</h3>
                <p className="text-sm text-muted-foreground">
                  {budgetOnTrack ? "You're on track" : "Needs attention"}
                </p>
              </div>
            </div>
            <div className="flex items-end justify-between mb-3">
              <span className="font-heading text-4xl font-extrabold tabular-nums">
                {Math.round(plan?.budget_adherence || 0)}%
              </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all ${
                  budgetOnTrack ? "bg-emerald-500" : "bg-amber-500"
                }`}
                style={{ width: `${Math.min(plan?.budget_adherence || 0, 100)}%` }}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                savingsRateGood ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-muted"
              }`}>
                <TrendingUp className={`w-5 h-5 ${savingsRateGood ? "text-emerald-600" : "text-muted-foreground"}`} />
              </div>
              <div>
                <h3 className="font-semibold">Savings Rate</h3>
                <p className="text-sm text-muted-foreground">
                  {savingsRateGood ? "Excellent!" : "Target: 20%"}
                </p>
              </div>
            </div>
            <div className="flex items-end justify-between mb-3">
              <span className="font-heading text-4xl font-extrabold tabular-nums">
                {Math.round(plan?.savings_rate || 0)}%
              </span>
              {savingsRateGood && (
                <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              )}
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all ${
                  savingsRateGood ? "bg-emerald-500" : "bg-blue-500"
                }`}
                style={{ width: `${Math.min((plan?.savings_rate || 0) / 30 * 100, 100)}%` }}
              />
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Top Spending */}
      <section>
        <h2 className="section-label">Top Spending Categories</h2>
        <Card className="border-border/50">
          <CardContent className="p-6">
            <div className="space-y-4">
              {plan?.top_spending_categories?.slice(0, 5).map((cat, i) => {
                const maxAmount = plan.top_spending_categories[0]?.amount || 1;
                const percentage = (cat.amount / maxAmount) * 100;
                
                return (
                  <div key={cat.category} className="flex items-center gap-4">
                    <span className="w-6 text-center font-semibold text-muted-foreground tabular-nums">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between mb-1.5">
                        <span className="font-medium truncate">{cat.category}</span>
                        <span className="amount ml-4">{formatCurrency(cat.amount)}</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary/60 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};
